package com.hyperether.goodjob.scenes.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material.Checkbox
import androidx.compose.material.CheckboxDefaults
import androidx.compose.material.MaterialTheme
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import com.hyperether.goodjob.boxCornerShape
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.briefcase_unselect
import com.hyperether.goodjob.resources.ic_search
import com.hyperether.goodjob.resources.ic_skills
import com.hyperether.goodjob.resources.industry
import com.hyperether.goodjob.resources.no_industries_available
import com.hyperether.goodjob.resources.no_matching_industries
import com.hyperether.goodjob.resources.no_matching_skills
import com.hyperether.goodjob.resources.no_matching_teams
import com.hyperether.goodjob.resources.no_skills_available
import com.hyperether.goodjob.resources.no_teams_available
import com.hyperether.goodjob.resources.select_industry
import com.hyperether.goodjob.resources.select_skill
import com.hyperether.goodjob.resources.select_team
import com.hyperether.goodjob.resources.skills
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.BorderColor
import com.hyperether.goodjob.theme.EmployeeDetailsText
import com.hyperether.goodjob.theme.InputBorderColor
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource

@Composable
fun SkillSelector(
    selectedSkills: MutableState<List<String>>,
    onSkillsSelected: (List<String>) -> Unit,
    availableSkills: MutableState<List<String>>,
    preselectedSkills: MutableState<List<String>>,
    onAddNewSkill: (String) -> Unit,
    onScrollStart: () -> Unit,
    onScrollEnd: () -> Unit
) {
    var searchText by remember { mutableStateOf("") }
    var isDropdownVisible by remember { mutableStateOf(false) }
    val focusManager = LocalFocusManager.current

    val listState = rememberLazyListState()
    val selectedListState = rememberLazyListState()
    val interactionOngoing = remember { mutableStateOf(false) }
    var previousSelectedListSize by remember { mutableStateOf(0) }
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress && interactionOngoing.value) {
            onScrollEnd()
            interactionOngoing.value = false
        }
    }

    LaunchedEffect(preselectedSkills.value) {
        preselectedSkills.value.forEach { preselectedItem ->
            if (preselectedItem in availableSkills.value && preselectedItem !in selectedSkills.value) {
                selectedSkills.value += preselectedItem
                onSkillsSelected(selectedSkills.value)
            }
        }
    }

    LaunchedEffect(selectedSkills.value.size) {
        if (selectedSkills.value.size > previousSelectedListSize) {
            selectedListState.animateScrollToItem(selectedSkills.value.size - 1)
        }
        previousSelectedListSize = selectedSkills.value.size
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
    ) {
        // Selected skills
        if (selectedSkills.value.isNotEmpty()) {
            LazyRow(
                state = selectedListState,
                horizontalArrangement = Arrangement.spacedBy(8.toScaleDp()),
                modifier = Modifier.fillMaxWidth()
            ) {
                items(selectedSkills.value) { skill ->
                    SelectedChip(
                        item = skill,
                        onRemove = {
                            selectedSkills.value = if (skill !in selectedSkills.value) {
                                selectedSkills.value + skill
                            } else {
                                selectedSkills.value - skill
                            }
                            onSkillsSelected(selectedSkills.value)
                        }
                    )
                }
            }
            Spacer(modifier = Modifier.height(8.toScaleDp()))
        }

        // Search Field
        OutlinedTextField(
            value = searchText,
            onValueChange = { searchText = it },
            label = {
                Text(
                    text = stringResource(Res.string.skills),
                    fontSize = 14.toScaleSp(),
                    color = TextColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            placeholder = {
                Text(
                    text = stringResource(Res.string.select_skill),
                    fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            singleLine = true,
            modifier = Modifier
                .fillMaxWidth()
                .onFocusChanged { focusState ->
                    if (focusState.isFocused) {
                        isDropdownVisible = true
                    } else {
                        coroutineScope.launch {
                            // On Web: Without delay, clicking on Dropdown item doesn't register onClick
                            // because focus lost instantly hides the Dropdown, delay is needed to allow the onClick event to trigger
                            delay(100)
                            isDropdownVisible = false
                        }
                    }
                },
            trailingIcon = {
                val trimmed = searchText.trim()
                if (trimmed.isNotEmpty()) {
                    IconButton(onClick = {
                        if (!availableSkills.value.contains(trimmed)) {
                            availableSkills.value + trimmed
                            onAddNewSkill(trimmed)
                        }
                        if (!selectedSkills.value.contains(trimmed)) selectedSkills.value + trimmed
                        searchText = ""
                    }) {
                        Icon(Icons.Default.Add, contentDescription = "Add Skill")
                    }
                }
            },
            keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(
                onDone = {
                    val trimmed = searchText.trim()
                    if (trimmed.isNotEmpty()) {
                        if (!availableSkills.value.contains(trimmed)) {
                            availableSkills.value + trimmed
                            onAddNewSkill(trimmed)
                            isDropdownVisible = false
                        }
                        if (!selectedSkills.value.contains(trimmed)) selectedSkills.value + trimmed
                        searchText = ""
                    }
                    focusManager.clearFocus()
                }
            ),
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )

        // Filtered skills
        val filteredSkills = availableSkills.value.filter {
            it.contains(searchText, ignoreCase = true) && !selectedSkills.value.contains(it)
        }

        if (isDropdownVisible) {
            Spacer(modifier = Modifier.height(8.toScaleDp()))

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(1.toScaleDp(), InputBorderColor, boxCornerShape())
                    .background(MaterialTheme.colors.surface)
                    .heightIn(min = 0.toScaleDp(), max = 200.toScaleDp())
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent()
                                if (event.changes.any { it.pressed }) {
                                    if (!interactionOngoing.value) {
                                        interactionOngoing.value = true
                                        onScrollStart()
                                    }
                                }

                                if (event.changes.all { !it.pressed } && !listState.isScrollInProgress) {
                                    if (interactionOngoing.value) {
                                        interactionOngoing.value = false
                                        onScrollEnd()
                                    }
                                }
                            }
                        }
                    }
            ) {
                LazyColumn(state = listState) {
                    items(filteredSkills) { skill ->
                        DropdownMenuItem(
                            modifier = Modifier.clip(RoundedCornerShape(4.toScaleDp())),
                            text = {
                                Text(
                                    text = skill,
                                    fontSize = 14.toScaleSp(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            },
                            onClick = {
                                searchText = ""
                                onScrollEnd()

                                selectedSkills.value = if (skill !in selectedSkills.value) {
                                    selectedSkills.value + skill
                                } else {
                                    selectedSkills.value - skill
                                }
                                onSkillsSelected(selectedSkills.value)
                            }
                        )
                    }

                    val trimmed = searchText.trim()
                    val message = when {
                        filteredSkills.isEmpty() && trimmed.isNotEmpty() ->
                            Res.string.no_matching_skills

                        filteredSkills.isEmpty() && trimmed.isEmpty() ->
                            Res.string.no_skills_available

                        else -> null
                    }

                    message?.let {
                        item {
                            Text(
                                text = stringResource(message),
                                fontSize = 14.toScaleSp(),
                                color = TextColor,
                                modifier = Modifier.padding(16.toScaleDp())
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SkillSelectorWeb(
    selectedSkills: MutableState<List<String>>,
    onSkillsSelected: (List<String>) -> Unit,
    availableSkills: MutableState<List<String>>,
    preselectedSkills: MutableState<List<String>>,
    onAddNewSkill: (String) -> Unit,
    onScrollStart: () -> Unit,
    onScrollEnd: () -> Unit
) {
    var searchText by remember { mutableStateOf("") }
    var isDropdownVisible by remember { mutableStateOf(false) }
    val focusManager = LocalFocusManager.current

    val listState = rememberLazyListState()
    val selectedListState = rememberLazyListState()
    val interactionOngoing = remember { mutableStateOf(false) }
    var previousSelectedListSize by remember { mutableStateOf(0) }

    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress && interactionOngoing.value) {
            onScrollEnd()
            interactionOngoing.value = false
        }
    }

    LaunchedEffect(preselectedSkills.value) {
        preselectedSkills.value.forEach { preselectedItem ->
            if (preselectedItem in availableSkills.value && preselectedItem !in selectedSkills.value) {
                selectedSkills.value += preselectedItem
                onSkillsSelected(selectedSkills.value)
            }
        }
    }

    LaunchedEffect(selectedSkills.value.size) {
        if (selectedSkills.value.size > previousSelectedListSize) {
            selectedListState.animateScrollToItem(selectedSkills.value.size - 1)
        }
        previousSelectedListSize = selectedSkills.value.size
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
    ) {
        // Selected skills
        if (selectedSkills.value.isNotEmpty()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                verticalAlignment = Alignment.Top
            ) {
                Spacer(modifier = Modifier.weight(1f))
                LazyRow(
                    state = selectedListState,
                    horizontalArrangement = Arrangement.spacedBy(8.toScaleDp()),
                    modifier = Modifier.weight(2f)
                ) {
                    items(selectedSkills.value) { skill ->
                        SelectedChip(
                            item = skill,
                            onRemove = {
                                selectedSkills.value = if (skill !in selectedSkills.value) {
                                    selectedSkills.value + skill
                                } else {
                                    selectedSkills.value - skill
                                }
                                onSkillsSelected(selectedSkills.value)
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.toScaleDp()))
        }

        Row(
            modifier = Modifier
                .fillMaxWidth(),
            verticalAlignment = Alignment.Top
        ) {
            Row(
                modifier = Modifier
                    .weight(1f)
                    .height(56.toScaleDp()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    painter = painterResource(Res.drawable.ic_skills),
                    modifier = Modifier.size(20.toScaleDp()),
                    contentDescription = null
                )
                Text(
                    text = stringResource(Res.string.skills),
                    color = EmployeeDetailsText,
                    fontSize = 14.toScaleSp(),
                    modifier = Modifier.padding(start = 20.toScaleDp()),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Column(
                modifier = Modifier.weight(2f)
            ) {

                OutlinedTextField(
                    value = searchText,
                    onValueChange = { searchText = it },
                    placeholder = {
                        Text(
                            text = stringResource(Res.string.select_skill),
                            fontSize = 14.toScaleSp(),
                            color = Color.LightGray,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    },
                    singleLine = true,
                    modifier = Modifier
                        .fillMaxWidth()
                        .onFocusChanged { focusState ->
                            if (focusState.isFocused) {
                                isDropdownVisible = true
                            } else {
                                coroutineScope.launch {
                                    // On Web: Without delay, clicking on Dropdown item doesn't register onClick
                                    // because focus lost instantly hides the Dropdown, delay is needed to allow the onClick event to trigger
                                    delay(100)
                                    isDropdownVisible = false
                                }
                            }
                        },
                    trailingIcon = {
                        val trimmed = searchText.trim()
                        if (trimmed.isNotEmpty()) {
                            IconButton(onClick = {
                                if (!availableSkills.value.contains(trimmed)) {
                                    availableSkills.value + trimmed
                                    onAddNewSkill(trimmed)
                                }
                                if (!selectedSkills.value.contains(trimmed)) selectedSkills.value + trimmed
                                searchText = ""
                            }) {
                                Icon(Icons.Default.Add, contentDescription = "Add Skill")
                            }
                        }
                    },
                    keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(
                        onDone = {
                            val trimmed = searchText.trim()
                            if (trimmed.isNotEmpty()) {
                                if (!availableSkills.value.contains(trimmed)) {
                                    availableSkills.value + trimmed
                                    onAddNewSkill(trimmed)
                                    isDropdownVisible = false
                                }
                                if (!selectedSkills.value.contains(trimmed)) selectedSkills.value + trimmed
                                searchText = ""
                            }
                            focusManager.clearFocus()
                        }
                    ),
                    colors = OutlinedTextFieldDefaults.colors(
                        disabledBorderColor = InputBorderColor,
                        unfocusedBorderColor = InputBorderColor,
                        disabledTextColor = TextColor,
                        focusedBorderColor = BlueApp,
                        cursorColor = BlueApp
                    )
                )

                // Filtered skills
                val filteredSkills = availableSkills.value.filter {
                    it.contains(searchText, ignoreCase = true) && !selectedSkills.value.contains(it)
                }

                if (isDropdownVisible) {
                    Spacer(modifier = Modifier.height(8.toScaleDp()))

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .border(
                                1.toScaleDp(),
                                InputBorderColor,
                                boxCornerShape()
                            )
                            .background(MaterialTheme.colors.surface)
                            .heightIn(min = 0.toScaleDp(), max = 200.toScaleDp())
                            .pointerInput(Unit) {
                                awaitPointerEventScope {
                                    while (true) {
                                        val event = awaitPointerEvent()
                                        if (event.changes.any { it.pressed }) {
                                            if (!interactionOngoing.value) {
                                                interactionOngoing.value = true
                                                onScrollStart()
                                            }
                                        }

                                        if (event.changes.all { !it.pressed } && !listState.isScrollInProgress) {
                                            if (interactionOngoing.value) {
                                                interactionOngoing.value = false
                                                onScrollEnd()
                                            }
                                        }
                                    }
                                }
                            }
                    ) {
                        LazyColumn(state = listState) {
                            items(filteredSkills) { skill ->
                                DropdownMenuItem(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(4.toScaleDp()))
                                        .pointerHoverIcon(PointerIcon.Hand),
                                    text = {
                                        Text(
                                            text = skill,
                                            fontSize = 14.toScaleSp(),
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    },
                                    onClick = {
                                        searchText = ""
                                        onScrollEnd()

                                        selectedSkills.value = if (skill !in selectedSkills.value) {
                                            selectedSkills.value + skill
                                        } else {
                                            selectedSkills.value - skill
                                        }
                                        onSkillsSelected(selectedSkills.value)
                                    }
                                )
                            }

                            val trimmed = searchText.trim()
                            val message = when {
                                filteredSkills.isEmpty() && trimmed.isNotEmpty() ->
                                    Res.string.no_matching_skills

                                filteredSkills.isEmpty() && trimmed.isEmpty() ->
                                    Res.string.no_skills_available

                                else -> null
                            }

                            message?.let {
                                item {
                                    Text(
                                        text = stringResource(message),
                                        fontSize = 14.toScaleSp(),
                                        color = TextColor,
                                        modifier = Modifier.padding(16.toScaleDp())
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SearchTeamSelector(
    teamsFromDb: List<Team>,
    onTeamsSelected: (List<Team>) -> Unit,
    onAddNewTeam: (String) -> Unit
) {
    var searchText by remember { mutableStateOf("") }
    val selectedTeams = remember { mutableStateListOf<Team>() }

    val focusManager = LocalFocusManager.current
    val listState = rememberLazyListState()

    val filteredTeams = teamsFromDb.filter {
        it.name?.contains(searchText, ignoreCase = true) == true
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(top = 10.toScaleDp())
    ) {
        Spacer(modifier = Modifier.height(8.toScaleDp()))

        // Search Field
        TextField(
            value = searchText,
            onValueChange = { searchText = it },
            placeholder = {
                Text(
                    text = stringResource(Res.string.select_team),
                    fontSize = 14.toScaleSp(),
                    color = TextColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            singleLine = true,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.toScaleDp())
                .border(1.toScaleDp(), Color.Gray, RoundedCornerShape(25.toScaleDp()))
                .clip(RoundedCornerShape(25.toScaleDp())),
            trailingIcon = {
                val trimmed = searchText.trim()
                if (trimmed.isNotEmpty()) {
                    IconButton(onClick = {
                        if (teamsFromDb.find { it.name == trimmed } == null) {
                            onAddNewTeam(trimmed)
                            searchText = ""
                        }
                    }) {
                        Icon(Icons.Default.Add, contentDescription = "Add Team")
                    }
                } else {
                    Icon(imageVector = Icons.Default.Search, contentDescription = "Search Icon")
                }
            },
            keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(
                onDone = {
                    val trimmed = searchText.trim()
                    if (trimmed.isNotEmpty() &&
                        teamsFromDb.find { it.name == trimmed } == null
                    ) {
                        onAddNewTeam(trimmed)
                        searchText = ""
                    }
                    focusManager.clearFocus()
                }
            ),
            colors = TextFieldDefaults.colors(
                unfocusedContainerColor = Color.Transparent,
                focusedContainerColor = Color.Transparent,
                selectionColors = TextSelectionColors(
                    backgroundColor = Color.Transparent,
                    handleColor = Color.Transparent
                ),
                focusedIndicatorColor = Color.Transparent,
                unfocusedIndicatorColor = Color.Transparent,
                disabledIndicatorColor = Color.Transparent
            )
        )

        Spacer(modifier = Modifier.height(8.toScaleDp()))

        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colors.surface)
        ) {
            LazyColumn(state = listState) {
                items(filteredTeams) { team ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable {
                                if (selectedTeams.contains(team)) {
                                    selectedTeams.remove(team)
                                } else {
                                    selectedTeams.add(team)
                                }
                                onTeamsSelected(selectedTeams)
                                focusManager.clearFocus()
                            }
                            .padding(horizontal = 12.toScaleDp(), vertical = 8.toScaleDp()),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Checkbox(
                            checked = selectedTeams.contains(team),
                            onCheckedChange = {
                                if (it) selectedTeams.add(team)
                                else selectedTeams.remove(team)
                                onTeamsSelected(selectedTeams)
                            },
                            colors = CheckboxDefaults.colors(
                                checkedColor = BlueApp,
                                uncheckedColor = Color.Gray,
                                checkmarkColor = Color.White
                            )
                        )
                        Spacer(modifier = Modifier.width(8.toScaleDp()))
                        Text(
                            text = team.name ?: "",
                            fontSize = 14.toScaleSp(),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }

                val trimmed = searchText.trim()
                val message = when {
                    filteredTeams.isEmpty() && trimmed.isNotEmpty() ->
                        Res.string.no_matching_teams

                    filteredTeams.isEmpty() && trimmed.isEmpty() ->
                        Res.string.no_teams_available

                    else -> null
                }

                if (message != null) {
                    item {
                        Text(
                            text = stringResource(message),
                            fontSize = 14.toScaleSp(),
                            color = TextColor,
                            modifier = Modifier.padding(16.toScaleDp())
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun SearchTeamSelectorWeb(
    text: String,
    teamsFromDb: List<Team>,
    onAddNewTeam: (String) -> Unit,
    onTextChange: (String) -> Unit,
) {
    BasicTextField(
        value = text,
        onValueChange = onTextChange,
        textStyle = TextStyle(fontSize = 12.toScaleSp(), color = Color.Black),
        singleLine = true,
        modifier = Modifier
            .width(240.toScaleDp())
            .height(45.toScaleDp())
            .background(Color.White, RoundedCornerShape(8.toScaleDp()))
            .border(1.toScaleDp(), BorderColor, RoundedCornerShape(8.toScaleDp()))
            .padding(8.toScaleDp()),
        decorationBox = { innerTextField ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxSize()
            ) {
                Image(
                    modifier = Modifier.size(24.toScaleDp()),
                    painter = painterResource(Res.drawable.ic_search),
                    contentDescription = "Search"
                )
                Spacer(modifier = Modifier.width(8.toScaleDp()))
                Box(Modifier.weight(1f), contentAlignment = Alignment.CenterStart) {
                    if (text.isEmpty()) {
                        androidx.compose.material.Text(
                            text = stringResource(Res.string.select_team),
                            fontSize = 14.toScaleSp(),
                            fontWeight = FontWeight.W400,
                            color = Color.Gray,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    innerTextField()
                }

                val trimmed = text.trim()
                if (trimmed.isNotEmpty()) {
                    IconButton(
                        modifier = Modifier.pointerCursorIfEnabled(true),
                        onClick = {
                            if (teamsFromDb.find { it.name == trimmed } == null) {
                                onAddNewTeam(trimmed)
                                onTextChange("")
                            }
                        }) {
                        Icon(Icons.Default.Add, contentDescription = "Add Team")
                    }
                }
            }
        },
        keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
        keyboardActions = KeyboardActions(
            onDone = {
                val trimmed = text.trim()
                if (trimmed.isNotEmpty()) {
                    if (teamsFromDb.find { it.name == trimmed } == null) {
                        onAddNewTeam(trimmed)
                        onTextChange("")
                    }
                }
            }
        )
    )
}

@Composable
fun IndustrySelector(
    allIndustries: List<String>,
    selectedIndustry: String?,
    onIndustrySelected: (String) -> Unit,
    onIndustryRemoved: () -> Unit,
    onAddNewIndustry: (String) -> Unit,
    onScrollStart: () -> Unit,
    onScrollEnd: () -> Unit
) {
    var searchText by remember { mutableStateOf("") }
    var isDropdownVisible by remember { mutableStateOf(false) }

    val focusManager = LocalFocusManager.current
    val listState = rememberLazyListState()
    val interactionOngoing = remember { mutableStateOf(false) }
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress && interactionOngoing.value) {
            onScrollEnd()
            interactionOngoing.value = false
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
    ) {
        // Selected industry
        if (!selectedIndustry.isNullOrEmpty()) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.toScaleDp()),
                modifier = Modifier.fillMaxWidth()
            ) {
                SelectedChip(
                    item = selectedIndustry,
                    onRemove = onIndustryRemoved
                )
            }
            Spacer(modifier = Modifier.height(8.toScaleDp()))
        }

        // Search Field
        OutlinedTextField(
            value = searchText,
            onValueChange = { searchText = it },
            label = {
                Text(
                    text = stringResource(Res.string.industry),
                    fontSize = 14.toScaleSp(),
                    color = TextColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            placeholder = {
                Text(
                    text = stringResource(Res.string.select_industry),
                    fontSize = 14.toScaleSp(),
                    color = Color.LightGray,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            },
            singleLine = true,
            modifier = Modifier
                .fillMaxWidth()
                .onFocusChanged { focusState ->
                    if (focusState.isFocused) {
                        isDropdownVisible = true
                    } else {
                        coroutineScope.launch {
                            // On Web: Without delay, clicking on Dropdown item doesn't register onClick
                            // because focus lost instantly hides the Dropdown, delay is needed to allow the onClick event to trigger
                            delay(100)
                            isDropdownVisible = false
                        }
                    }
                },
            trailingIcon = {
                val trimmed = searchText.trim()
                if (trimmed.isNotEmpty()) {
                    IconButton(onClick = {
                        if (allIndustries.find { it == trimmed } == null) {
                            onAddNewIndustry(trimmed)
                            searchText = ""
                            isDropdownVisible = false
                            onScrollEnd()
                            focusManager.clearFocus()
                        }
                    }) {
                        Icon(Icons.Default.Add, contentDescription = "Add Team")
                    }
                }
            },
            keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(
                onDone = {
                    val trimmed = searchText.trim()
                    if (trimmed.isNotEmpty()) {
                        if (allIndustries.find { it == trimmed } == null) {
                            onAddNewIndustry(trimmed)
                            searchText = ""
                            isDropdownVisible = false
                            onScrollEnd()
                        }
                    }
                    focusManager.clearFocus()
                }
            ),
            colors = OutlinedTextFieldDefaults.colors(
                disabledBorderColor = InputBorderColor,
                unfocusedBorderColor = InputBorderColor,
                disabledTextColor = TextColor,
                focusedBorderColor = BlueApp,
                cursorColor = BlueApp
            )
        )

        // Filtered Industries
        val filteredIndustries = allIndustries.filter {
            it.contains(
                searchText,
                ignoreCase = true
            ) && selectedIndustry != it
        }

        if (isDropdownVisible) {
            Spacer(modifier = Modifier.height(8.toScaleDp()))

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .border(1.toScaleDp(), InputBorderColor, boxCornerShape())
                    .background(MaterialTheme.colors.surface)
                    .heightIn(min = 0.toScaleDp(), max = 200.toScaleDp())
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent()
                                if (event.changes.any { it.pressed }) {
                                    if (!interactionOngoing.value) {
                                        interactionOngoing.value = true
                                        onScrollStart()
                                    }
                                }

                                if (event.changes.all { !it.pressed } && !listState.isScrollInProgress) {
                                    if (interactionOngoing.value) {
                                        interactionOngoing.value = false
                                        onScrollEnd()
                                    }
                                }
                            }
                        }
                    }
            ) {
                LazyColumn(state = listState) {
                    items(filteredIndustries) { industry ->
                        DropdownMenuItem(
                            modifier = Modifier.clip(RoundedCornerShape(4.toScaleDp())),
                            text = {
                                Text(
                                    text = industry,
                                    fontSize = 14.toScaleSp(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            },
                            onClick = {
                                searchText = ""
                                isDropdownVisible = false
                                onScrollEnd()
                                onIndustrySelected(industry)
                                focusManager.clearFocus()
                            }
                        )
                    }

                    val trimmed = searchText.trim()
                    val message = when {
                        filteredIndustries.isEmpty() && trimmed.isNotEmpty() ->
                            Res.string.no_matching_industries

                        filteredIndustries.isEmpty() && trimmed.isEmpty() ->
                            Res.string.no_industries_available

                        else -> null
                    }

                    if (message != null) {
                        item {
                            Text(
                                text = stringResource(message),
                                fontSize = 14.toScaleSp(),
                                color = TextColor,
                                modifier = Modifier.padding(16.toScaleDp())
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun IndustrySelectorWeb(
    allIndustries: List<String>,
    selectedIndustry: String?,
    onIndustrySelected: (String) -> Unit,
    onIndustryRemoved: () -> Unit,
    onAddNewIndustry: (String) -> Unit,
    onScrollStart: () -> Unit,
    onScrollEnd: () -> Unit
) {
    val focusManager = LocalFocusManager.current
    var searchText by remember { mutableStateOf("") }
    var isDropdownVisible by remember { mutableStateOf(false) }

    val listState = rememberLazyListState()
    val interactionOngoing = remember { mutableStateOf(false) }

    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress && interactionOngoing.value) {
            onScrollEnd()
            interactionOngoing.value = false
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        // Selected industry
        if (!selectedIndustry.isNullOrEmpty()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                verticalAlignment = Alignment.Top
            ) {
                Spacer(modifier = Modifier.weight(1f))
                Box(modifier = Modifier.weight(2f)) {
                    SelectedChip(
                        item = selectedIndustry,
                        onRemove = onIndustryRemoved
                    )
                }
            }
            Spacer(modifier = Modifier.height(8.toScaleDp()))
        }

        Row(
            modifier = Modifier
                .fillMaxWidth(),
            verticalAlignment = Alignment.Top
        ) {
            Row(
                modifier = Modifier
                    .weight(1f)
                    .height(56.toScaleDp()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    painter = painterResource(Res.drawable.briefcase_unselect),
                    modifier = Modifier.size(20.toScaleDp()),
                    contentDescription = null
                )
                Text(
                    text = stringResource(Res.string.industry),
                    color = EmployeeDetailsText,
                    fontSize = 14.toScaleSp(),
                    modifier = Modifier.padding(start = 20.toScaleDp()),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Column(
                modifier = Modifier.weight(2f)
            ) {

                // Search Field
                OutlinedTextField(
                    value = searchText,
                    onValueChange = { searchText = it },
                    placeholder = {
                        Text(
                            text = stringResource(Res.string.select_industry),
                            fontSize = 14.toScaleSp(),
                            color = Color.LightGray,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    },
                    singleLine = true,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.toScaleDp())
                        .onFocusChanged { focusState ->
                            if (focusState.isFocused) {
                                isDropdownVisible = true
                            } else {
                                coroutineScope.launch {
                                    // On Web: Without delay, clicking on Dropdown item doesn't register onClick
                                    // because focus lost instantly hides the Dropdown, delay is needed to allow the onClick event to trigger
                                    delay(100)
                                    isDropdownVisible = false
                                }
                            }
                        },
                    trailingIcon = {
                        val trimmed = searchText.trim()
                        if (trimmed.isNotEmpty()) {
                            IconButton(onClick = {
                                if (allIndustries.find { it == trimmed } == null) {
                                    onAddNewIndustry(trimmed)
                                    searchText = ""
                                    isDropdownVisible = false
                                    onScrollEnd()
                                    focusManager.clearFocus()
                                }
                            }, modifier = Modifier.pointerHoverIcon(PointerIcon.Hand)) {
                                Icon(Icons.Default.Add, contentDescription = "Add Team")
                            }
                        }
                    },
                    keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(
                        onDone = {
                            val trimmed = searchText.trim()
                            if (trimmed.isNotEmpty()) {
                                if (allIndustries.find { it == trimmed } == null) {
                                    onAddNewIndustry(trimmed)
                                    searchText = ""
                                    isDropdownVisible = false
                                    onScrollEnd()
                                }
                            }
                            focusManager.clearFocus()
                        }
                    ),
                    colors = OutlinedTextFieldDefaults.colors(
                        disabledBorderColor = InputBorderColor,
                        unfocusedBorderColor = InputBorderColor,
                        disabledTextColor = TextColor,
                        focusedBorderColor = BlueApp,
                        cursorColor = BlueApp
                    )
                )

                // Filtered Industries
                val filteredIndustries = allIndustries.filter {
                    it.contains(
                        searchText,
                        ignoreCase = true
                    ) && selectedIndustry != it
                }

                if (isDropdownVisible) {
                    Spacer(modifier = Modifier.height(8.toScaleDp()))

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .border(
                                1.toScaleDp(),
                                InputBorderColor,
                                boxCornerShape()
                            )
                            .background(MaterialTheme.colors.surface)
                            .heightIn(min = 0.toScaleDp(), max = 200.toScaleDp())
                            .pointerInput(Unit) {
                                awaitPointerEventScope {
                                    while (true) {
                                        val event = awaitPointerEvent()
                                        if (event.changes.any { it.pressed }) {
                                            if (!interactionOngoing.value) {
                                                interactionOngoing.value = true
                                                onScrollStart()
                                            }
                                        }

                                        if (event.changes.all { !it.pressed } && !listState.isScrollInProgress) {
                                            if (interactionOngoing.value) {
                                                interactionOngoing.value = false
                                                onScrollEnd()
                                            }
                                        }
                                    }
                                }
                            }
                    ) {
                        LazyColumn(
                            state = listState,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            items(filteredIndustries) { industry ->
                                Row(
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    DropdownMenuItem(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clip(RoundedCornerShape(4.toScaleDp()))
                                            .pointerHoverIcon(PointerIcon.Hand),
                                        text = {
                                            Text(
                                                text = industry,
                                                fontSize = 14.toScaleSp(),
                                                maxLines = 1,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                        },
                                        onClick = {
                                            searchText = ""
                                            isDropdownVisible = false
                                            onIndustrySelected(industry)
                                            focusManager.clearFocus()
                                            onScrollEnd()
                                        }
                                    )

                                }
                            }

                            val trimmed = searchText.trim()
                            val message = when {
                                filteredIndustries.isEmpty() && trimmed.isNotEmpty() ->
                                    Res.string.no_matching_industries

                                filteredIndustries.isEmpty() && trimmed.isEmpty() ->
                                    Res.string.no_industries_available

                                else -> null
                            }

                            if (message != null) {
                                item {
                                    Text(
                                        text = stringResource(message),
                                        fontSize = 14.toScaleSp(),
                                        color = TextColor,
                                        modifier = Modifier.padding(16.toScaleDp())
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
